"use strict";
/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { ChunkRenderLayerFrontend } from "#src/chunk_manager/frontend.js";
import { trackableRenderScaleTarget } from "#src/render_scale_statistics.js";
import { RenderLayer, VisibilityTrackedRenderLayer } from "#src/renderlayer.js";
import { SharedWatchableValue } from "#src/shared_watchable_value.js";
import {
  filterVisibleSources,
  SLICEVIEW_RENDERLAYER_RPC_ID
} from "#src/sliceview/base.js";
import { constantWatchableValue } from "#src/trackable_value.js";
import { HistogramSpecifications } from "#src/webgl/empirical_cdf.js";
export class SliceViewRenderLayer extends RenderLayer {
  constructor(chunkManager, multiscaleSource, options) {
    super();
    this.chunkManager = chunkManager;
    this.multiscaleSource = multiscaleSource;
    const { renderScaleTarget = trackableRenderScaleTarget(1) } = options;
    this.renderScaleTarget = renderScaleTarget;
    this.renderScaleHistogram = options.renderScaleHistogram;
    this.transform = options.transform;
    this.localPosition = options.localPosition;
    this.rpcTransfer = options.rpcTransfer || {};
    this.dataHistogramSpecifications = this.registerDisposer(
      options.dataHistogramSpecifications ?? new HistogramSpecifications(
        constantWatchableValue([]),
        constantWatchableValue([]),
        constantWatchableValue([])
      )
    );
    this.registerDisposer(
      this.dataHistogramSpecifications.visibility.changed.add(
        this.redrawNeeded.dispatch
      )
    );
  }
  rpcId = null;
  rpcTransfer = {};
  localPosition;
  channelCoordinateSpace;
  transform;
  renderScaleTarget;
  renderScaleHistogram;
  // This is only used by `ImageRenderLayer` currently, but is defined here because
  // `sliceview/frontend.ts` is responsible for providing the texture buffers used for accumulating
  // histograms.
  dataHistogramSpecifications;
  getDataHistogramCount() {
    return this.dataHistogramSpecifications.visibleHistograms;
  }
  /**
   * Currently visible sources for this render layer.
   */
  visibleSources = /* @__PURE__ */ new Map();
  /**
   * Cached list of sources in `visibleSources`, ordered by voxel size.
   *
   * Truncated to zero length when `visibleSources` changes to indicate that it is invalid.
   */
  visibleSourcesList_ = [];
  getSources(options) {
    return this.multiscaleSource.getSources(options);
  }
  addSource(source, chunkTransform) {
    const { visibleSources } = this;
    const info = visibleSources.get(source);
    if (info !== void 0) {
      ++info.refCount;
      info.chunkTransform = chunkTransform;
    } else {
      visibleSources.set(source, { source, refCount: 1, chunkTransform });
      this.visibleSourcesList_.length = 0;
    }
  }
  removeSource(source) {
    const { visibleSources } = this;
    const info = visibleSources.get(source);
    if (info.refCount !== 1) {
      --info.refCount;
    } else {
      visibleSources.delete(source);
      this.visibleSourcesList_.length = 0;
    }
  }
  get visibleSourcesList() {
    const { visibleSources, visibleSourcesList_ } = this;
    if (visibleSourcesList_.length === 0 && visibleSources.size !== 0) {
      for (const info of visibleSources.values()) {
        visibleSourcesList_.push(info);
      }
      visibleSourcesList_.sort((a, b) => {
        return Math.abs(a.chunkTransform.chunkToLayerTransformDet) - Math.abs(b.chunkTransform.chunkToLayerTransformDet);
      });
    }
    return visibleSourcesList_;
  }
  initializeCounterpart() {
    const sharedObject = this.registerDisposer(
      new ChunkRenderLayerFrontend(this.layerChunkProgressInfo)
    );
    const rpc = this.chunkManager.rpc;
    sharedObject.RPC_TYPE_ID = this.RPC_TYPE_ID;
    sharedObject.initializeCounterpart(rpc, {
      localPosition: this.registerDisposer(
        SharedWatchableValue.makeFromExisting(rpc, this.localPosition)
      ).rpcId,
      renderScaleTarget: this.registerDisposer(
        SharedWatchableValue.makeFromExisting(rpc, this.renderScaleTarget)
      ).rpcId,
      ...this.rpcTransfer
    });
    this.rpcId = sharedObject.rpcId;
  }
  get gl() {
    return this.chunkManager.chunkQueueManager.gl;
  }
  setGLBlendMode(gl, renderLayerNum) {
    if (renderLayerNum > 0) {
      gl.enable(WebGL2RenderingContext.BLEND);
      gl.blendFunc(
        WebGL2RenderingContext.SRC_ALPHA,
        WebGL2RenderingContext.ONE_MINUS_SRC_ALPHA
      );
    } else {
      gl.disable(WebGL2RenderingContext.BLEND);
    }
  }
  filterVisibleSources(sliceView, sources) {
    return filterVisibleSources(sliceView, this, sources);
  }
}
SliceViewRenderLayer.prototype.RPC_TYPE_ID = SLICEVIEW_RENDERLAYER_RPC_ID;
export class SliceViewPanelRenderLayer extends VisibilityTrackedRenderLayer {
  draw(renderContext, attachment) {
    renderContext;
    attachment;
  }
  isReady(renderContext, attachment) {
    renderContext;
    attachment;
    return true;
  }
}
